import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Bug, Lightbulb, Clock, HelpCircle, Play, CheckCircle, XCircle, Loader2, AlertTriangle, Wrench, Cog, ShieldAlert, ExternalLink, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const FEEDBACK_STATUS = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Clock },
  needs_info: { label: 'Needs Info', color: 'bg-orange-100 text-orange-700', icon: HelpCircle },
  rejected: { label: 'Returned', color: 'bg-red-100 text-red-700', icon: XCircle },
  promoted: { label: 'Promoted', color: 'bg-indigo-100 text-indigo-700', icon: ArrowUpRight },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Play },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-200 text-slate-700', icon: XCircle }
};

const ISSUE_STATUS = {
  open: { label: 'Queued', color: 'bg-blue-100 text-blue-700', icon: Clock },
  rejected: { label: 'Returned', color: 'bg-red-100 text-red-700', icon: XCircle },
  decision_made: { label: 'Decision Made', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const TYPE_CONFIG = {
  bug: { icon: Bug, color: 'bg-red-500/20', iconColor: 'text-red-400', label: 'Bug' },
  feature: { icon: Lightbulb, color: 'bg-emerald-500/20', iconColor: 'text-emerald-400', label: 'Feature' },
  execution_headwind: { icon: AlertTriangle, color: 'bg-amber-500/20', iconColor: 'text-amber-400', label: 'Headwind' },
  capability_gap: { icon: Wrench, color: 'bg-sky-500/20', iconColor: 'text-sky-400', label: 'Gap' },
  process_friction: { icon: Cog, color: 'bg-slate-500/20', iconColor: 'text-slate-400', label: 'Friction' },
  red_line_violation: { icon: ShieldAlert, color: 'bg-rose-500/20', iconColor: 'text-rose-400', label: 'Red Line' },
};

export default function SubmittedFeedbackList({ onSelect, autoSelectId, onAutoSelectHandled }) {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: myFeedback = [], isLoading: loadingFeedback } = useQuery({
    queryKey: ['myFeedback', user?.email],
    queryFn: () => base44.entities.Feedback.filter({ submitted_by: user.email }, '-created_date'),
    enabled: !!user?.email
  });

  const { data: myIssues = [], isLoading: loadingIssues } = useQuery({
    queryKey: ['myIssues', user?.email],
    queryFn: () => base44.entities.Issue.filter({ reported_by: user.email }, '-created_date'),
    enabled: !!user?.email
  });

  // Merge both lists with source markers
  const allSubmissions = React.useMemo(() => {
    const feedbackItems = myFeedback.map(f => ({ ...f, _source: 'feedback', _type: f.type || 'bug' }));
    const issueItems = myIssues
      .filter(i => i.source === 'manual') // Only show manually-submitted issues (not auto-promoted from reports)
      .map(i => ({ ...i, _source: 'issue', _type: i.category || 'execution_headwind' }));
    return [...feedbackItems, ...issueItems].sort((a, b) => {
      const dateA = new Date(a.created_date || a.reported_at || 0);
      const dateB = new Date(b.created_date || b.reported_at || 0);
      return dateB - dateA;
    });
  }, [myFeedback, myIssues]);

  const isLoading = loadingFeedback || loadingIssues;

  // Auto-select a specific item when deep-linked
  React.useEffect(() => {
    if (autoSelectId && allSubmissions.length > 0) {
      const item = allSubmissions.find(f => f.id === autoSelectId);
      if (item && item._source === 'feedback') {
        onSelect(item);
      }
      onAutoSelectHandled?.();
    }
  }, [autoSelectId, allSubmissions]);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading...
      </div>
    );
  }

  if (allSubmissions.length === 0) {
    return (
      <div className="p-6 text-center text-slate-500 text-sm">
        You haven't submitted anything yet.
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      {allSubmissions.map((item) => {
        const isFeedback = item._source === 'feedback';
        const typeCfg = TYPE_CONFIG[item._type] || TYPE_CONFIG.bug;
        const TypeIcon = typeCfg.icon;

        let statusCfg;
        if (isFeedback) {
          statusCfg = FEEDBACK_STATUS[item.status] || FEEDBACK_STATUS.open;
        } else {
          statusCfg = ISSUE_STATUS[item.status] || ISSUE_STATUS.open;
        }
        const StatusIcon = statusCfg.icon;

        const date = item.created_date || item.reported_at;

        if (!isFeedback) {
          // Issue item — show as read-only with link to Issues page
          return (
            <div key={`issue-${item.id}`}
              className="w-full text-left px-4 py-3 border-b border-slate-800 flex items-start gap-3 hover:bg-slate-800/30 transition-colors">
              <div className={`p-1.5 rounded shrink-0 mt-0.5 ${typeCfg.color}`}>
                <TypeIcon className={`w-3.5 h-3.5 ${typeCfg.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">{item.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${statusCfg.color} text-[10px] px-1.5 py-0 font-medium`}>
                    <StatusIcon className="w-2.5 h-2.5 mr-0.5" />{statusCfg.label}
                  </Badge>
                  <Badge className="bg-slate-700 text-slate-300 text-[10px] px-1.5 py-0">{typeCfg.label}</Badge>
                  {date && <span className="text-[10px] text-slate-500">{format(new Date(date), 'MMM d, yyyy')}</span>}
                </div>
              </div>
              <Link to={createPageUrl('Issues') + '?tab=issues&highlight=' + item.id}
                className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-0.5 shrink-0 mt-1"
                onClick={(e) => e.stopPropagation()}>
                View <ExternalLink className="w-2.5 h-2.5" />
              </Link>
            </div>
          );
        }

        // Feedback item — clickable to edit
        return (
          <button key={`feedback-${item.id}`} onClick={() => onSelect(item)}
            className="w-full text-left px-4 py-3 border-b border-slate-800 hover:bg-slate-800/60 transition-colors flex items-start gap-3">
            <div className={`p-1.5 rounded shrink-0 mt-0.5 ${typeCfg.color}`}>
              <TypeIcon className={`w-3.5 h-3.5 ${typeCfg.iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{item.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${statusCfg.color} text-[10px] px-1.5 py-0 font-medium`}>
                  <StatusIcon className="w-2.5 h-2.5 mr-0.5" />{statusCfg.label}
                </Badge>
                {item.captain && (
                  <Badge className={`${item.captain_override ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-sky-500/20 text-sky-300 border border-sky-500/40'} text-[10px] px-1.5 py-0 font-medium`}
                    title={item.captain + (item.captain_override ? ' (admin override)' : ' has the plane')}>
                    ✈ {(item.captain.split('@')[0] || item.captain).slice(0, 10)}
                  </Badge>
                )}
                {date && <span className="text-[10px] text-slate-500">{format(new Date(date), 'MMM d, yyyy')}</span>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}